import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FilePdfIcon, MicrosoftExcelLogoIcon } from "@phosphor-icons/react";
import api from "@/lib/api";

// DOC-DL-STYLE-01 · per-document Download PDF / Export to Excel (server-built, permission-checked).
export async function downloadDoc(type, id, fmt) {
  try {
    const res = await api.get(`/doc-export/${type}/${id}/${fmt}`, { responseType: "blob" });
    const cd = res.headers?.["content-disposition"] || "";
    const name = (cd.match(/filename="?([^";]+)"?/) || [])[1] || `${type}.${fmt}`;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch (e) {
    let msg = "Download failed";
    try { msg = JSON.parse(await e?.response?.data?.text())?.detail || msg; } catch (_) { /* keep default */ }
    toast.error(typeof msg === "string" ? msg : "Download failed");
  }
}

export const DocDownloadButtons = ({ type, id, testPrefix, className = "" }) => {
  const [busy, setBusy] = useState("");
  const run = async (fmt) => { setBusy(fmt); await downloadDoc(type, id, fmt); setBusy(""); };
  const btn = "px-3 py-2 rounded-sm border text-sm font-semibold flex items-center gap-2 whitespace-nowrap disabled:opacity-50 transition-colors";
  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      <button type="button" disabled={!id || !!busy} onClick={() => run("pdf")} data-testid={`${testPrefix}-download-pdf`}
        className={`${btn} border-rose-500/40 text-rose-300 hover:bg-rose-500/10`}>
        <FilePdfIcon size={14} weight="bold" />{busy === "pdf" ? "Preparing…" : "Download PDF"}
      </button>
      <button type="button" disabled={!id || !!busy} onClick={() => run("xlsx")} data-testid={`${testPrefix}-export-excel`}
        className={`${btn} border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10`}>
        <MicrosoftExcelLogoIcon size={14} weight="bold" />{busy === "xlsx" ? "Preparing…" : "Export to Excel"}
      </button>
    </div>
  );
};

// Compact icon pair for register rows / Actions cells.
export const DocDownloadIcons = ({ type, id, testPrefix }) => (
  <>
    <button type="button" title="Download PDF" data-testid={`${testPrefix}-download-pdf`} onClick={() => downloadDoc(type, id, "pdf")}
      className="p-1.5 rounded-sm text-rose-300 hover:bg-rose-500/10 border border-rose-500/30"><FilePdfIcon size={13} weight="bold" /></button>
    <button type="button" title="Export to Excel" data-testid={`${testPrefix}-export-excel`} onClick={() => downloadDoc(type, id, "xlsx")}
      className="p-1.5 rounded-sm text-emerald-300 hover:bg-emerald-500/10 border border-emerald-500/30"><MicrosoftExcelLogoIcon size={13} weight="bold" /></button>
  </>
);

// Style No per line exactly as printed (saved value, else exact linked source line).
export function useDocStyles(type, id) {
  const [styles, setStyles] = useState([]);
  useEffect(() => {
    if (!type || !id) { setStyles([]); return; }
    let live = true;
    api.get(`/doc-export/${type}/${id}/styles/lines`).then(r => live && setStyles(r.data?.styles || [])).catch(() => live && setStyles([]));
    return () => { live = false; };
  }, [type, id]);
  return styles;
}
